/**
 * Showing money without touching it.
 *
 * Every function here works on the string the server sent. Nothing parses it
 * into a number: JavaScript has one numeric type and it is a float, so
 * `Number("1183.30")` is already a different value than the ledger holds, and
 * the difference is invisible until it is large enough to matter.
 *
 * Grouping and rounding are string surgery — split at the point, walk the
 * digits, carry by hand. No arithmetic on the value itself.
 *
 * Rounding happens for display and nowhere else. The exact figure is what the
 * server measured and what every other part of the system holds; two decimals
 * is what a person can read. Both are shown: the short form on the line, the
 * exact one when you hover it.
 */

/** A figure that came from the API and must not be computed with. */
export type Money = string;

const GROUP = " ";
/** The same narrow space, between a figure and the unit it carries. */
const THIN = " "; // narrow no-break space: groups digits without a gap wide enough to read as two numbers

//: Money is quoted in USDT, and cents are the smallest unit anybody acts on.
export const PLACES = 2;

const DIGITS = "0123456789";

/**
 * Add one to a string of digits, carrying by hand.
 *
 * The next digit comes from a lookup rather than from `Number(d) + 1`. The
 * conversion would be harmless on one character, but the rule against turning
 * money into a number is absolute on purpose: an exception here is the first
 * one, and the second is always easier to argue for.
 */
function incremented(digits: string): string {
  const out = digits.split("");
  for (let index = out.length - 1; index >= 0; index -= 1) {
    const digit = out[index] ?? "0";
    if (digit !== "9") {
      out[index] = DIGITS.charAt(DIGITS.indexOf(digit) + 1);
      return out.join("");
    }
    out[index] = "0";
  }
  // Every digit was a nine: the number grew a place.
  return "1" + out.join("");
}

/**
 * The same value at a fixed number of decimals, rounded half up.
 *
 * Half up because that is what a person expects when they read a price, and
 * because the alternative — truncating — would show a loss as smaller than it
 * is exactly half the time.
 */
export function toPlaces(value: Money, places: number = PLACES): string {
  const negative = value.startsWith("-");
  const body = negative || value.startsWith("+") ? value.slice(1) : value;
  const point = body.indexOf(".");
  const whole = point === -1 ? body : body.slice(0, point);
  const fraction = point === -1 ? "" : body.slice(point + 1);

  let digits = whole + fraction.slice(0, places).padEnd(places, "0");
  const next = fraction.charAt(places);
  if (next !== "" && next >= "5") digits = incremented(digits);

  const cut = digits.length - places;
  // No decimals asked for means no decimal point either: a trailing dot is
  // not a smaller number, it is a typo on screen.
  const shortened =
    places === 0 ? digits : digits.slice(0, cut) + "." + digits.slice(cut);
  // A minus in front of a zero reads as a loss that did not happen.
  const zero = /^0*(\.0*)?$/.test(shortened);
  return (negative && !zero ? "-" : "") + shortened;
}

/**
 * What share of a whole a part is, as a percentage between zero and a hundred.
 *
 * For geometry only -- the width of a bar -- and never for a figure anybody
 * reads. It still refuses to go through a float: `BigInt` is exact for
 * integers, so aligning both values to the same number of decimals turns an
 * exact division of two decimals into an exact division of two integers. The
 * rule this file exists to enforce is not "money must not be divided", it is
 * "money must not become a float", and the second is the one that loses value.
 *
 * Returns the whole percent as a string, so the figure never has to become a
 * number at all on its way to a width. Zero for a whole that is zero or
 * negative: a bar with no track has no fill, and a share of nothing is not a
 * hundred percent.
 */
export function percentOf(part: Money, whole: Money): string {
  const places = Math.max(_decimals(part), _decimals(whole));
  const numerator = _scaled(part, places);
  const denominator = _scaled(whole, places);
  if (denominator <= 0n) return "0";

  const share = (numerator * 100n) / denominator;
  if (share <= 0n) return "0";
  return (share >= 100n ? 100n : share).toString();
}

function _decimals(value: Money): number {
  const point = value.indexOf(".");
  return point === -1 ? 0 : value.length - point - 1;
}

/** The same value as an exact integer, shifted left by `places` decimals. */
function _scaled(value: Money, places: number): bigint {
  const negative = value.startsWith("-");
  const body = negative || value.startsWith("+") ? value.slice(1) : value;
  const point = body.indexOf(".");
  const whole = point === -1 ? body : body.slice(0, point);
  const fraction = point === -1 ? "" : body.slice(point + 1);
  const digits = (whole + fraction.padEnd(places, "0")) || "0";
  const magnitude = BigInt(digits);
  return negative ? -magnitude : magnitude;
}


export function isNegative(value: Money): boolean {
  return value.startsWith("-");
}

export function isZero(value: Money): boolean {
  return /^-?0(\.0*)?$/.test(value);
}

/**
 * Group the integer digits and keep everything else exactly as it arrived.
 *
 * The fractional part is untouched, including trailing zeros: "0.10" and "0.1"
 * are the same number and different measurements, and the one recorded is the
 * one shown.
 */
export function grouped(value: Money): string {
  const sign = value.startsWith("-") || value.startsWith("+") ? value.slice(0, 1) : "";
  const body = sign === "" ? value : value.slice(1);
  const point = body.indexOf(".");
  const whole = point === -1 ? body : body.slice(0, point);
  const rest = point === -1 ? "" : body.slice(point);

  let out = "";
  for (let index = whole.length; index > 0; index -= 3) {
    const start = Math.max(0, index - 3);
    out = whole.slice(start, index) + (out === "" ? "" : GROUP + out);
  }
  return sign + (out === "" ? whole : out) + rest;
}

/**
 * The same, with an explicit sign on anything that is not zero.
 *
 * A result of "3.13" and a result of "-3.13" must not differ only by colour:
 * somebody who cannot distinguish the two colours would read them as the same
 * number.
 */
export function signed(value: Money): string {
  if (isZero(value)) return grouped(value);
  return isNegative(value) ? grouped(value) : "+" + grouped(value);
}

/**
 * The same value with the point moved right, exactly.
 *
 * A rate arrives as a fraction — 0.1042 — and is read as a percentage. Moving
 * the point is string surgery for the same reason the rest of this file is:
 * multiplying by a hundred would turn an exact decimal into a float, and the
 * figure on the bar would stop matching the figure beside it.
 */
export function shifted(value: Money, places: number): string {
  const negative = value.startsWith("-");
  const body = negative || value.startsWith("+") ? value.slice(1) : value;
  const point = body.indexOf(".");
  const whole = point === -1 ? body : body.slice(0, point);
  const fraction = point === -1 ? "" : body.slice(point + 1);

  const moved = fraction.padEnd(places, "0");
  const digits = whole + moved.slice(0, places);
  const rest = moved.slice(places);
  const trimmed = digits.replace(/^0+(?=\d)/, "");
  return (negative ? "-" : "") + trimmed + (rest === "" ? "" : "." + rest);
}

/**
 * A rate as a percentage: exact, rounded for the eye.
 *
 * The sign is set off from the figure by a thin space, as the rest of the
 * product and the server already set it. There were two percent formatters in
 * this codebase disagreeing about exactly this — one printed `83.3 %`, the
 * other `+1.80%` — and a reader met both on one screen.
 */
export function percent(value: Money, places: number = PLACES): string {
  return toPlaces(shifted(value, 2), places) + THIN + "%";
}

/**
 * A percentage someone typed, as the exact fraction the server stores.
 *
 * The inverse of moving the point right: "15" is "0.15", "2.5" is "0.025".
 * Anything that is not plain digits, or reaches a hundred, comes back as
 * written so the server refuses it rather than this function guessing.
 */
export function fromPercent(typed: string): string {
  const text = typed.trim();
  if (!/^\d{1,2}(\.\d+)?$/.test(text)) return text;
  const [whole = "0", fraction = ""] = text.split(".");
  const digits = `${whole.padStart(2, "0")}${fraction}`.replace(/0+$/, "");
  return digits === "" ? "0" : `0.${digits}`;
}

/**
 * A whole percentage that was already measured, with its unit attached.
 *
 * `percentOf` returns the number alone, and four call sites were each gluing
 * the sign on by hand — three of them without the narrow space the rest of the
 * product uses, and one of them in JSX text where the linter refuses the
 * character outright.
 */
export function sharePercent(whole: string): string {
  return whole + THIN + "%";
}

/** The same, with an explicit sign on anything that is not zero. */
export function signedPercent(value: Money, places: number = PLACES): string {
  const shown = toPlaces(shifted(value, 2), places);
  return (isZero(shown) || isNegative(shown) ? shown : "+" + shown) + THIN + "%";
}

/**
 * A fraction as a bar width, clamped to the bar.
 *
 * Clamped for the eye only — the figure printed beside the bar is never
 * capped, because beating the goal is worth seeing. Done by comparing digits
 * rather than by parsing: this file has no numbers in it, and a percentage is
 * measured to the same precision as the money it came from.
 */
export function barWidth(share: Money): string {
  if (isNegative(share)) return "0%";
  const shown = toPlaces(shifted(share, 2), 1);
  const whole = shown.slice(0, shown.indexOf(".")).replace(/^0+(?=\d)/, "");
  const full = whole.length > 3 || (whole.length === 3 && whole >= "100");
  return full ? "100%" : shown + "%";
}

/**
 * A large figure at scanning precision: `50M`, `1.2B`, `452K`.
 *
 * For a column that is compared rather than reconciled. Daily turnover printed
 * in full is eight grouped digits per row, and eleven rows of them is a wall
 * the eye has to count through to find the one that is smaller. The exact
 * figure stays one hover away, as it does everywhere else.
 *
 * String surgery, like the rest of this file: dividing by a million would turn
 * an exact decimal into a float, and this project does not put floats near
 * money even when the result is only being looked at.
 */
export function compactMoney(value: Money): string {
  const negative = value.startsWith("-");
  const body = negative || value.startsWith("+") ? value.slice(1) : value;
  const point = body.indexOf(".");
  const whole = (point === -1 ? body : body.slice(0, point)).replace(/^0+(?=\d)/, "");

  const units: readonly [number, string][] = [
    [10, "B"],
    [7, "M"],
    [4, "K"],
  ];
  for (const [digits, suffix] of units) {
    if (whole.length < digits) continue;
    const head = whole.slice(0, whole.length - (digits - 1));
    const next = whole.slice(whole.length - (digits - 1), whole.length - (digits - 2));
    // One decimal, and only when it says something: 1.2B is worth a character,
    // 50.0M is not.
    const shown = head.length < 3 && next !== "0" ? `${head}.${next}` : head;
    return (negative ? "-" : "") + shown + suffix;
  }
  return grouped(toPlaces(value, 0));
}

/** What a balance looks like on screen: two decimals, grouped. */
export function money(value: Money, places: number = PLACES): string {
  return grouped(toPlaces(value, places));
}

/**
 * What a result looks like on screen: the same, with an explicit sign.
 *
 * The sign is decided after rounding. A loss of a hundredth of a cent shows as
 * zero, and "-0.00" would say the account lost something it did not.
 */
export function signedMoney(value: Money, places: number = PLACES): string {
  return signed(toPlaces(value, places));
}

/**
 * Compare two exact decimals without ever making them numbers.
 *
 * Returns a negative number when `left` is smaller, zero when they are equal
 * and positive when it is larger — the shape `Array.sort` expects, and the only
 * thing callers should read from it.
 *
 * Written by hand because the alternative is `Number()`, which is banned here
 * for the reason that matters: a decimal that becomes a float has already lost
 * whatever precision it was carrying, and the loss is silent. Comparing digits
 * costs nothing and cannot lose anything.
 */
export function compareDecimal(left: Money, right: Money): number {
  const negative = left.startsWith("-");
  if (negative !== right.startsWith("-")) return negative ? -1 : 1;

  const sign = negative ? -1 : 1;
  const [leftWhole = "0", leftPart = ""] = left.replace("-", "").split(".");
  const [rightWhole = "0", rightPart = ""] = right.replace("-", "").split(".");

  const a = leftWhole.replace(/^0+(?=\d)/, "");
  const b = rightWhole.replace(/^0+(?=\d)/, "");
  if (a.length !== b.length) return a.length < b.length ? -sign : sign;
  if (a !== b) return a < b ? -sign : sign;

  const width = Math.max(leftPart.length, rightPart.length);
  const x = leftPart.padEnd(width, "0");
  const y = rightPart.padEnd(width, "0");
  if (x === y) return 0;
  return x < y ? -sign : sign;
}

/** Whether an exact decimal is at least as large as a threshold. */
export function atLeast(value: Money, threshold: Money): boolean {
  return compareDecimal(value, threshold) >= 0;
}

/** Whether an exact decimal is no larger than a threshold. */
export function atMost(value: Money, threshold: Money): boolean {
  return compareDecimal(value, threshold) <= 0;
}


/**
 * An instrument's price, at the precision the price itself needs.
 *
 * Money on the account is two decimals — a balance of 19.98 USDT is exactly
 * that. A price is not a balance: at two decimals HOMEUSDT's average entry of
 * 0.00616518 printed as `0.01`, which is not a rounding of it but a different
 * number, and every cheap instrument in the book printed the same `0.01`
 * whatever it actually cost.
 *
 * So the rule follows the figure rather than the column: at or above one, two
 * decimals, because that is where a price behaves like an amount; below one,
 * four significant digits, which distinguishes 0.006165 from 0.01298 and stops
 * where a reader stops caring. Trailing zeros are dropped — `20.90` keeps its
 * shape because it came from a two-decimal branch, and `0.5011` does not gain
 * a fifth digit it never had.
 *
 * Never parsed into a number: the digits are counted in the string, the same
 * way every other figure in this module is measured.
 */
export function price(value: Money): string {
  const negative = value.startsWith("-");
  const body = negative || value.startsWith("+") ? value.slice(1) : value;
  const point = body.indexOf(".");
  const whole = point === -1 ? body : body.slice(0, point);
  const fraction = point === -1 ? "" : body.slice(point + 1);

  if (whole.replace(/^0+/, "") !== "") return money(value);

  // Below one: skip the zeros that only place the number, then keep four
  // digits of the part that carries it.
  let leading = 0;
  while (leading < fraction.length && fraction.charAt(leading) === "0") leading += 1;
  const shown = toPlaces(body, Math.min(leading + 4, 12)).replace(/0+$/, "");
  const trimmed = shown.endsWith(".") ? shown.slice(0, -1) : shown;
  return negative ? `-${trimmed}` : trimmed;
}

/**
 * How many decimals the exchange quotes an instrument at, read from prices the
 * exchange itself produced — its last price and the grid's order prices, which
 * are placed on the tick. An average or a target is a division and carries as
 * many digits as the division gave; shown at this precision it reads the way
 * the exchange would print it. Null when no such price is at hand.
 */
export function quoteDecimals(prices: readonly (string | null | undefined)[]): number | null {
  let most: number | null = null;
  for (const text of prices) {
    if (text == null || text === "") continue;
    const fraction = text.split(".")[1] ?? "";
    const places = fraction.replace(/0+$/, "").length;
    most = Math.max(most ?? 0, places);
  }
  return most === null ? null : Math.min(most, 10);
}

/**
 * A price at the exchange's precision, or by the general price rule. A price
 * of one or more keeps at least two decimals: a reference read as `105` says
 * the exchange dropped its zeros, not that 105.25 is worth printing as 105.
 */
export function quote(value: Money, decimals: number | null): string {
  if (decimals === null) return price(value);
  const body = value.replace(/^[+-]/, "");
  const whole = body.split(".")[0] ?? "";
  const atLeastOne = whole.replace(/^0+/, "") !== "";
  return toPlaces(value, atLeastOne ? Math.max(decimals, 2) : decimals);
}

/**
 * The exact sum of decimals, as a decimal. Digits are added as integers scaled
 * to the longest fraction, so nothing is ever a float on the way.
 */
export function sumDecimal(values: readonly Money[]): Money {
  const places = Math.max(0, ...values.map((value) => value.split(".")[1]?.length ?? 0));
  const scaled = (value: Money): bigint => {
    const negative = value.startsWith("-");
    const [whole = "0", part = ""] = value.replace("-", "").split(".");
    const digits = BigInt(`${whole}${part.padEnd(places, "0")}`);
    return negative ? -digits : digits;
  };
  const total = values.reduce((sum, value) => sum + scaled(value), 0n);
  const negative = total < 0n;
  const digits = (negative ? -total : total).toString().padStart(places + 1, "0");
  const whole = digits.slice(0, digits.length - places);
  const part = digits.slice(digits.length - places);
  return `${negative ? "-" : ""}${whole}${places > 0 ? `.${part}` : ""}`;
}
